import React, { useEffect, useState } from "react";
import { Avatar, Button, Divider, List, Row, Skeleton, Table, Tag } from "antd";
import mentorReviewStyles from "./mentorReview.module.css";
import { Link, useNavigate } from "react-router-dom";
import { Content } from "antd/lib/layout/layout";
import { useUserStore } from "../../../context/UserStore";
import { formatDate } from "../../../utils/dateFormat";

const REMINDER_ROW_LIMIT = 10;

export default function MentorReview() {
  const navigate = useNavigate();
  const [loadingMentorDashboard, setLoadingMentorDashboard] = useState(true);
  const [mentors, setMentors] = useState([]);
  const [overdueConversations, setOverdueConversations] = useState([]);
  const [dueSoonConversations, setDueSoonConversations] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    overdue: false,
    dueSoon: false,
  });
  const user = useUserStore((state) => state.user);

  const getMentorDashbordData = async () => {
    await fetch(`${process.env.REACT_APP_API_URL}/api/v1/users/mentors/mentor_dashboard`, {
      method: "get",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${user.token}`,
      },
      type: "json",
    })
      .then((response) => {
        if (!response.ok) {
          throw Error(response.statusText);
        }
        return response.json();
      })
      .then((response) => {
        setMentors(response.mentors.data);
        setOverdueConversations(response.overdue_conversations.data);
        setDueSoonConversations(response.due_soon_conversations.data);
        setLoadingMentorDashboard(false);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const viewEmployee = (id) => {
    navigate(`/my-employees/${id}`);
  };

  // Only Coaching records carry a sentiment. Anything else gets a neutral border rather
  // than being reported as positive by default.
  const sentimentColor = (feedbackType) => {
    if (!feedbackType) {
      return "border-gray-300";
    }
    return feedbackType == "negative" ? "border-red-500" : "border-green-500";
  };

  const returnIcon = (typeId) => {
    switch (typeId) {
      case 0:
        return "/assets/images/coaching-icon.png";
      case 1:
        return "/assets/images/observation-icon.png";
      case 2:
        return "/assets/images/dialogue-icon.png";
      case 3:
        return "/assets/images/dialogue-icon.png";
      default:
        return "/assets/images/dialogue-icon.png";
    }
  };

  const reminderColumns = [
    {
      title: "Employee",
      dataIndex: "attributes.full_name",
      render: (_id, record) => record.attributes.full_name,
    },
    {
      title: "Status",
      dataIndex: "attributes.conversation_status_label",
      render: (_id, record) => record.attributes.conversation_status_label,
    },
    {
      title: "Next due",
      dataIndex: "attributes.next_conversation_due_on",
      render: (_id, record) =>
        formatDate(record.attributes.next_conversation_due_on),
    },
    {
      title: "Action",
      dataIndex: "id",
      render: (id) => (
        <Button
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold px-4 rounded"
          onClick={() => viewEmployee(id)}
        >
          View employee
        </Button>
      ),
    },
  ];

  const toggleSection = (key) => {
    setExpandedSections((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const renderReminderSection = ({ title, color, data, sectionKey, emptyLabel }) => {
    const isExpanded = expandedSections[sectionKey];
    const visibleRows = isExpanded ? data : data.slice(0, REMINDER_ROW_LIMIT);
    const hasOverflow = data.length > REMINDER_ROW_LIMIT;

    return (
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Tag color={color}>{title}</Tag>
            <span className="text-sm text-gray-600">{data.length} employees</span>
          </div>
          {hasOverflow && (
            <Button type="link" onClick={() => toggleSection(sectionKey)}>
              {isExpanded ? "Show less" : "Show more"}
            </Button>
          )}
        </div>
        <Table
          columns={reminderColumns}
          dataSource={visibleRows}
          locale={{ emptyText: emptyLabel }}
          pagination={false}
          rowKey="id"
          size="small"
        />
      </div>
    );
  };

  useEffect(() => {
    getMentorDashbordData();
  }, []);

  return (
    <>
      <Row
        className={`py-8 mb-10 flex items-center justify-center rounded-lg shadow-lg ${mentorReviewStyles.headerBar}`}
        style={{ background: "linear-gradient(135deg, #2485B2 0%, #70AEDD 100%)", color: "#fff" }}
      >
        <h1 className="text-4xl md:text-4xl text-center font-bold text-white tracking-wide">
          Mentoring Conversations
        </h1>
      </Row>
      <div className="flex flex-col items-center md:items-start">
        <div className="w-full">
          <div className="bg-white rounded-lg p-0">
            <div className="flex justify-center md:justify-start space-x-2 mb-4">
              <button
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold px-4 py-2 rounded"
                onClick={() => navigate("/my-employees/feedback/new")}
              >
                <svg
                  className="w-4 h-4 inline-block mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                Add Mentor Conversation
              </button>
            </div>
          </div>
        </div>
      </div>
      <Content>
        <Skeleton loading={loadingMentorDashboard} active>
          {renderReminderSection({
            title: "Overdue conversations",
            color: "red",
            data: overdueConversations,
            sectionKey: "overdue",
            emptyLabel: "No overdue conversations.",
          })}
          {renderReminderSection({
            title: "Due soon",
            color: "gold",
            data: dueSoonConversations,
            sectionKey: "dueSoon",
            emptyLabel: "No conversations due within 3 days.",
          })}
        </Skeleton>
        <Divider />
        <Row>
          <h2 className="text-lg text-left text-blue">Mentor Conversations</h2>
        </Row>
        <Skeleton loading={loadingMentorDashboard} active>
          <List
            dataSource={mentors}
            itemLayout="horizontal"
            renderItem={(item) => (
              <List.Item
                className={`border-l-4 ${sentimentColor(item.attributes.feedback_type)} pl-2 pr-4`}
                actions={[
                  <Link
                    to={`/my-employees/${item.attributes.mentored_employee.id}/${item.attributes.mentor_type_name.toLowerCase()}/${item.id}/view`}
                  >
                    View Mentor
                  </Link>,
                  formatDate(item.attributes.occurred_at),
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      shape={"square"}
                      size={40}
                      style={{ backgroundColor: "blue" }}
                      src={returnIcon(item.attributes.mentor_type_id)}
                    />
                  }
                  title={`${item.attributes.mentored_employee.first_name} ${item.attributes.mentored_employee.last_name}`}
                  description={item.attributes.mentor_type_name}
                />
              </List.Item>
            )}
          />
        </Skeleton>
      </Content>
    </>
  );
}
